import React, { useState, useRef, useEffect } from 'react';
import { verifyMfaSetup, verifyMfa } from './api';
import { useAuth } from './AuthContext';
import MerkLogo from './MerkLogo';

export default function MfaVerifyPage({ userId, mode, qrCode, onBack, brand }) {
  const { loginWithToken } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  function handleChange(index, value) {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every(d => d !== '') && newCode.join('').length === 6) {
      submitCode(newCode.join(''));
    }
  }

  function handleKeyDown(index, e) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      submitCode(pasted);
    }
  }

  async function submitCode(token) {
    setError('');
    setLoading(true);
    try {
      const verifyFn = mode === 'setup' ? verifyMfaSetup : verifyMfa;
      const data = await verifyFn(userId, token);
      loginWithToken(data.token, data.user);
    } catch (err) {
      setError(err.message);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <MerkLogo brand={brand} />
          <h1 className="text-2xl font-bold text-slate-100">
            {mode === 'setup' ? 'MFA instellen' : 'Verificatie'}
          </h1>
          <p className="text-slate-400 mt-1">
            {mode === 'setup' ? 'Scan de QR-code met je authenticator app' : 'Voer de code in uit je authenticator app'}
          </p>
        </div>
        <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 space-y-6">
          {mode === 'setup' && qrCode && (
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-xl">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" />
              </div>
            </div>
          )}
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3 text-center">Voer de 6-cijferige code in</label>
            <div className="flex gap-3 justify-center" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  className="w-12 h-14 text-center text-xl font-bold bg-slate-700/50 border border-slate-600/50 rounded-xl text-slate-100 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500 transition-colors"
                />
              ))}
            </div>
          </div>
          {loading && <p className="text-center text-slate-400 text-sm">Verifiëren...</p>}
          <button type="button" onClick={onBack} className="w-full py-3 bg-slate-700 text-slate-300 font-medium rounded-xl hover:bg-slate-600 transition-all">
            Terug
          </button>
        </div>
      </div>
    </div>
  );
}
